import { createClient } from "@sanity/client";
import imageUrlBuilder from "@sanity/image-url";

export const client = createClient({
  projectId: "lhkgj8kr",
  dataset: "production",
  useCdn: true, // set to `false` to bypass the edge cache
  apiVersion: "2025-04-08", // use current date (YYYY-MM-DD) to target the latest API version. Note: this should always be hard coded. Setting API version based on a dynamic value (e.g. new Date()) may break your application at a random point in the future.
  // token: process.env.SANITY_SECRET_TOKEN // Needed for certain operations like updating content, accessing drafts or using draft perspectives
});

const builder = imageUrlBuilder(client);

export const urlForImage = (source) => {
  return builder.image(source);
};

export const sanityClient = client;

// Pagination
export const getPostsPaginated = async (page = 1, limit = 12) => {
  const start = (page - 1) * limit;
  const end = start + limit;

  const query = `{
    "posts": *[_type == "post"] | order(publishedAt desc) [${start}...${end}] {
      _id,
      title,
      slug,
      publishedAt,
      mainImage {
        asset,
        alt
      },
      author->{
        name,
        image,
        slug
      },
      categories[]->{
        title
      }
    },
    "total": count(*[_type == "post"])
  }`;

  const result = await client.fetch(query);

  return {
    posts: result.posts,
    total: result.total,
    currentPage: page,
    totalPages: Math.ceil(result.total / limit),
    hasNext: end < result.total,
    hasPrev: page > 1,
  };
};

export async function getPosts() {
  const query = `
    *[_type == "post"] | order(publishedAt desc){
      _id,
      title,
      slug,
      publishedAt,
      mainImage,
      author->{
        name,
        image,
        slug
      },
      categories[]->{
        title
      }
    }
  `;
  return client.fetch(query);
}

export async function getRecentPosts(limit = 10) {
  return client.fetch(
    `*[_type == "post"] | order(_createdAt desc)[0...${limit}]`
  );
}

export async function getPostBySlug(slug) {
  const query = `
    *[_type == "post" && slug.current == $slug][0]{
      _id,
      title,
      slug,
      publishedAt,
      mainImage,
      body,
      author->{
        name,
        image,
        slug,
        bio
      },
      categories[]->{
        title,
        description
      }
    }
  `;
  const params = { slug };
  return client.fetch(query, params);
}

export const getProductsPaginated = async (
  page = 1,
  limit = 12,
  categorySlug = null,
  status = "Available"
) => {
  const start = (page - 1) * limit;
  const end = start + limit;

  // Build filter conditions
  let filters = `_type == "product"`;
  if (status) {
    filters += ` && status == "${status}"`;
  }
  if (categorySlug && categorySlug !== "all") {
    filters += ` && category->slug.current == "${categorySlug}"`;
  }

  const query = `{
    "products": *[${filters}] | order(createdAt desc) [${start}...${end}] {
      _id,
      title,
      slug,
      sku,
      description,
      price,
      priceUSD,
      originalValue,
      originalValueUSD,
      condition,
      status,
      images[] {
        asset->,
        alt
      },
      videoUrl,
      category-> {
        name,
        slug
      },
      tags,
      pawnDetails,
      featured,
      createdAt
    },
    "total": count(*[${filters}])
  }`;

  const result = await client.fetch(query);
  return {
    products: result.products,
    total: result.total,
    currentPage: page,
    totalPages: Math.ceil(result.total / limit),
    hasNext: end < result.total,
    hasPrev: page > 1,
  };
};

// Get all marketplace categories
export async function getMarketplaceCategories() {
  const query = `
    *[_type == "marketplaceCategory"] | order(name asc){
      _id,
      name,
      slug,
      description,
      icon
    }
  `;
  return client.fetch(query);
}

// Get all products (without pagination)
export async function getAllProducts(status = "Available") {
  const query = `
    *[_type == "product" && status == "${status}"] | order(createdAt desc){
      _id,
      title,
      slug,
      sku,
      description,
      price,
      priceUSD,
      originalValue,
      originalValueUSD,
      condition,
      status,
      images[] {
        asset->,
        alt
      },
      videoUrl,
      category-> {
        name,
        slug
      },
      tags,
      pawnDetails,
      featured,
      createdAt
    }
  `;
  return client.fetch(query);
}

// Get products by category
export async function getProductsByCategory(categorySlug, limit = null) {
  let query = `
    *[_type == "product" && category->slug.current == "${categorySlug}" && status == "Available"] | order(createdAt desc)
  `;

  if (limit) {
    query += `[0...${limit}]`;
  }

  query += `{
    _id,
    title,
    slug,
    sku,
    description,
    price,
    priceUSD,
    originalValue,
    originalValueUSD,
    condition,
    status,
    images[] {
      asset->,
      alt
    },
    videoUrl,
    category-> {
      name,
      slug
    },
    tags,
    pawnDetails,
    featured,
    createdAt
  }`;

  return client.fetch(query);
}

// Get featured products
export async function getFeaturedProducts(limit = 6) {
  const query = `
    *[_type == "product" && featured == true && status == "Available"] | order(createdAt desc)[0...${limit}]{
      _id,
      title,
      slug,
      sku,
      description,
      price,
      priceUSD,
      originalValue,
      originalValueUSD,
      condition,
      status,
      images[] {
        asset->,
        alt
      },
      videoUrl,
      category-> {
        name,
        slug
      },
      tags,
      pawnDetails,
      featured,
      createdAt
    }
  `;
  return client.fetch(query);
}

// Get recent products
export async function getRecentProducts(limit = 10) {
  return client.fetch(
    `*[_type == "product" && status == "Available"] | order(createdAt desc)[0...${limit}]{
      _id,
      title,
      slug,
      price,
      priceUSD,
      originalValue,
      originalValueUSD,
      condition,
      images[] {
        asset->,
        alt
      },
      category-> {
        name,
        slug
      },
      createdAt
    }`
  );
}

// Search products
export async function searchProducts(searchTerm, limit = 20) {
  const query = `
    *[_type == "product" && status == "Available" && (
      title match "${searchTerm}*" ||
      description match "${searchTerm}*" ||
      "${searchTerm}" in tags ||
      category->name match "${searchTerm}*"
    )] | order(createdAt desc)[0...${limit}]{
      _id,
      title,
      slug,
      sku,
      description,
      price,
      priceUSD,
      originalValue,
      originalValueUSD,
      condition,
      status,
      images[] {
        asset->,
        alt
      },
      category-> {
        name,
        slug
      },
      tags,
      createdAt
    }
  `;
  return client.fetch(query);
}

// Get single product by slug
export async function getProductBySlug(slug) {
  const query = `
    *[_type == "product" && slug.current == $slug][0]{
      _id,
      title,
      slug,
      sku,
      description,
      price,
      priceUSD,
      originalValue,
      originalValueUSD,
      condition,
      status,
      images[] {
        asset->,
        alt
      },
      videoUrl,
      category-> {
        name,
        slug,
        description
      },
      tags,
      pawnDetails,
      featured,
      createdAt
    }
  `;
  const params = { slug };
  return client.fetch(query, params);
}

// Get product statistics (for admin dashboard)
export async function getProductStats() {
  const query = `{
    "total": count(*[_type == "product"]),
    "available": count(*[_type == "product" && status == "Available"]),
    "sold": count(*[_type == "product" && status == "Sold"]),
    "reserved": count(*[_type == "product" && status == "Reserved"]),
    "featured": count(*[_type == "product" && featured == true]),
    "categories": count(*[_type == "marketplaceCategory"]),
    "withUSDPricing": count(*[_type == "product" && defined(priceUSD)])
  }`;
  return client.fetch(query);
}

export default client;
